/**
 * Team Participation Requirements — CRUD page for cross-functional participation tracking.
 * Corresponds to spec's TEAM_PARTICIPATION_REQUIREMENTS input sheet.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Building2, Calendar, Loader2, Pencil, Send, Trash2, Users, Check, X } from 'lucide-react';
import {
  getTeamParticipation,
  getPriorities,
  getWorkUnits,
  createTeamParticipation,
  updateTeamParticipation,
  deleteTeamParticipation,
} from '../../../lib/planningEngineService';
import { runTimePhasedPlacement } from '../../../lib/timePhasedPlanningService';
import { usePlanningEngine } from './PlanningEngineContext';
import type {
  TeamParticipation,
  ParticipationTristate,
  ParticipationRequestStatus,
  ParticipationPeriodCommitment,
} from '../../../lib/planningEngineModule.types';
import { PlanningEntityListPage } from './PlanningEntityListPage';
import {
  participationWithdrawAllowed,
  sendParticipationRequest,
  withdrawParticipationRequest,
} from '../../../lib/planningParticipationService';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { listWorkspaceMembers } from '../../../lib/workspaceService';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { OrgUnitTreePicker } from '../../../components/prioritization/OrgUnitTreePicker';
import { Select } from '../../../components/ds';
import { Button } from '../../../components/ui/Button';
import { filterOrgUnitsForWorkspace, formatOrgUnitPathLabel } from '../../../lib/orgUnitTree';
import type { OrgUnit } from '../../../types/prioritization/enterpriseIdentity';
import type { TableColumnWithRender } from '../../../components/collection/types';
import {
  fillPeriodCommitmentsFromWindow,
  rollupRequestStatus,
} from '../../../lib/participationPeriod';

const REQUEST_STATUS_OPTIONS: { value: ParticipationRequestStatus; label: string }[] = [
  { value: 'requested', label: 'Requested' },
  { value: 'tentative', label: 'Tentative' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'declined', label: 'Declined' },
];

// ─── Tri-state badge ──────────────────────────────────────────────────────────

function TristateBadge({ value }: { value: ParticipationTristate }) {
  const cls =
    value === 'yes'
      ? 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/20 dark:text-[var(--text-ter)]'
      : value === 'partial'
        ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:text-[var(--mustard)]'
        : 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/20 dark:text-[var(--coral)]';
  const label = value === 'yes' ? 'Yes' : value === 'partial' ? 'Partial' : 'No';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{label}</span>
  );
}

function TristateSelect({
  value,
  onChange,
}: {
  value: ParticipationTristate;
  onChange: (v: ParticipationTristate) => void;
}) {
  return (
    <select
      className="text-sm border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1 bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)]"
      value={value}
      onChange={(e) => onChange(e.target.value as ParticipationTristate)}
    >
      <option value="yes">Yes</option>
      <option value="partial">Partial</option>
      <option value="no">No</option>
    </select>
  );
}

// ─── Create/Edit Modal ────────────────────────────────────────────────────────

type DraftParticipation = {
  priorityId: string;
  workUnitId: string;
  requiredTeam: string;
  /** Org unit picked from the chart (hierarchy type `org`, migration 719). '' = text-only. */
  requiredOrgUnitId: string;
  requiredCapability: string;
  capacityRequiredWu: string;
  capacityConfirmed: ParticipationTristate;
  capabilityPresent: ParticipationTristate;
  committed: ParticipationTristate;
  owner: string;
  /** Accountable contact as a user reference; '' = text-only owner label. */
  ownerUserId: string;
  riskIfUnavailable: string;
  notes: string;
  committedWindowStart: string;
  committedWindowEnd: string;
  participationDeadline: string;
  /** The person who will answer this request — required before it can be sent. */
  participationOwnerId: string;
  requestStatus: ParticipationRequestStatus;
  periodCommitments: ParticipationPeriodCommitment[];
};

const EMPTY_DRAFT: DraftParticipation = {
  priorityId: '',
  workUnitId: '',
  requiredTeam: '',
  requiredOrgUnitId: '',
  requiredCapability: '',
  capacityRequiredWu: '',
  capacityConfirmed: 'no',
  capabilityPresent: 'no',
  committed: 'no',
  owner: '',
  ownerUserId: '',
  riskIfUnavailable: '',
  notes: '',
  committedWindowStart: '',
  committedWindowEnd: '',
  participationDeadline: '',
  participationOwnerId: '',
  requestStatus: 'requested',
  periodCommitments: [],
};

function participationToDraft(p: TeamParticipation): DraftParticipation {
  return {
    priorityId: p.priorityId ?? '',
    workUnitId: p.workUnitId ?? '',
    requiredTeam: p.requiredTeam,
    requiredOrgUnitId: p.requiredOrgUnitId ?? '',
    requiredCapability: p.requiredCapability ?? '',
    capacityRequiredWu: p.capacityRequiredWu != null ? String(p.capacityRequiredWu) : '',
    capacityConfirmed: p.capacityConfirmed,
    capabilityPresent: p.capabilityPresent,
    committed: p.committed,
    owner: p.owner ?? '',
    ownerUserId: p.ownerUserId ?? '',
    riskIfUnavailable: p.riskIfUnavailable ?? '',
    notes: p.notes ?? '',
    committedWindowStart: p.committedWindowStart ?? '',
    committedWindowEnd: p.committedWindowEnd ?? '',
    participationDeadline: p.participationDeadline ?? '',
    participationOwnerId: p.participationOwnerId ?? '',
    requestStatus: p.requestStatus ?? 'requested',
    periodCommitments: p.periodCommitments ?? [],
  };
}

type PriorityOption = { id: string; name: string };
type MemberOption = { userId: string; name: string };
type WUOption = { id: string; title: string; priorityId: string | null };

function ParticipationModal({
  draft,
  setDraft,
  priorities,
  workUnits,
  members,
  orgUnits,
  workspaceId,
  row,
  saving,
  onSave,
  onCancel,
  onHandedOff,
  title,
}: {
  draft: DraftParticipation;
  setDraft: (d: DraftParticipation) => void;
  priorities: PriorityOption[];
  workUnits: WUOption[];
  members: MemberOption[];
  /** The org chart, so the required team is a node, not a typed string. */
  orgUnits: OrgUnit[];
  workspaceId: string | null;
  /** The persisted row, when editing — carries the hand-off state. */
  row: TeamParticipation | null;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
  onHandedOff: (action: 'send' | 'withdraw') => void;
  title: string;
}) {
  const filteredWUs = draft.priorityId
    ? workUnits.filter((wu) => wu.priorityId === draft.priorityId)
    : workUnits;

  const [handoffBusy, setHandoffBusy] = useState(false);
  const [handoffError, setHandoffError] = useState<string | null>(null);
  const sent = row?.requestedAt != null;
  // withdraw_participation_request (migration 690) takes the person who sent the
  // request or a workspace admin; draw Withdraw only for them (§2.8).
  const userId = useUserStore((st) => st.userId);
  const isAdmin = useUserStore((st) => st.can('admin.settings'));
  const mayWithdraw = participationWithdrawAllowed({ userId, requestedBy: row?.requestedBy ?? null, isAdmin });

  const handoff = async (action: 'send' | 'withdraw') => {
    if (!row) return;
    setHandoffBusy(true);
    setHandoffError(null);
    try {
      if (action === 'send') {
        await sendParticipationRequest({
          participationId: row.id,
          ownerId: draft.participationOwnerId || null,
          deadline: draft.participationDeadline || null,
        });
      } else {
        await withdrawParticipationRequest(row.id);
      }
      onHandedOff(action);
    } catch (err) {
      setHandoffError(err instanceof Error ? err.message : String(err));
    } finally {
      setHandoffBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] shadow-xl w-full max-w-lg max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)] dark:border-[var(--rule)]">
          <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{title}</h2>
          <button onClick={onCancel} className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {/* Priority */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
              Priority <span className="text-[var(--coral)]">*</span>
            </label>
            <select
              className="w-full text-sm border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1.5 bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)]"
              value={draft.priorityId}
              onChange={(e) => setDraft({ ...draft, priorityId: e.target.value, workUnitId: '' })}
            >
              <option value="">— Select a priority —</option>
              {priorities.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          {/* Work Unit — optional (null = applies to ALL WUs for the priority) */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
              Work Unit
              <span className="text-[var(--text-ter)] font-normal ml-1">(leave blank to apply to all work units)</span>
            </label>
            <select
              className="w-full text-sm border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1.5 bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)]"
              value={draft.workUnitId}
              onChange={(e) => setDraft({ ...draft, workUnitId: e.target.value })}
              disabled={!draft.priorityId}
            >
              <option value="">ALL — applies to every work unit for this priority</option>
              {filteredWUs.map((wu) => (
                <option key={wu.id} value={wu.id}>{wu.title}</option>
              ))}
            </select>
          </div>
          {/* Required Team — an org-chart node (hierarchy type `org`), with the text
              label kept as display denormalization. Picking a node fills the label;
              the label stays editable for teams the chart does not model yet. */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
              Required Team <span className="text-[var(--coral)]">*</span>
            </label>
            {orgUnits.length > 0 ? (
              <OrgUnitTreePicker
                units={orgUnits}
                workspaceId={workspaceId}
                selectedId={draft.requiredOrgUnitId}
                onSelect={(id) => {
                  const unit = orgUnits.find((u) => u.id === id);
                  setDraft({
                    ...draft,
                    requiredOrgUnitId: id,
                    requiredTeam: unit ? unit.displayName || unit.name : draft.requiredTeam,
                  });
                }}
                placeholder="Pick the team from the org chart"
              />
            ) : (
              <p className="text-[11px] text-[var(--text-ter)] mb-1">
                No org chart in this workspace yet — the team is recorded as text only.
              </p>
            )}
            <div className="mt-2 flex items-center gap-2">
              <input
                className="flex-1 text-sm border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1.5 bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)]"
                placeholder="Display label, e.g. Finance, Legal, Data Platform"
                value={draft.requiredTeam}
                onChange={(e) => setDraft({ ...draft, requiredTeam: e.target.value })}
              />
              {draft.requiredOrgUnitId ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="!px-2 !py-1 text-[var(--text-ter)] hover:text-[var(--coral)] whitespace-nowrap"
                  title="Keep the label but drop the link to the org unit"
                  onClick={() => setDraft({ ...draft, requiredOrgUnitId: '' })}
                >
                  Unlink
                </Button>
              ) : null}
            </div>
            <p className="mt-1 text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              {draft.requiredOrgUnitId
                ? 'Linked to the org chart — renames follow the node.'
                : 'Not linked to an org unit. A typed name cannot be renamed, rolled up, or matched to a capacity pool.'}
            </p>
          </div>
          {/* Required Capability */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">Required Capability</label>
            <input
              className="w-full text-sm border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1.5 bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)]"
              placeholder="Specific capability required from this team"
              value={draft.requiredCapability}
              onChange={(e) => setDraft({ ...draft, requiredCapability: e.target.value })}
            />
          </div>
          {/* Capacity Required WU */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">Capacity Required (WU)</label>
            <input
              type="number"
              min={0}
              step={0.5}
              className="w-32 text-sm border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1.5 bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)]"
              placeholder="0"
              value={draft.capacityRequiredWu}
              onChange={(e) => setDraft({ ...draft, capacityRequiredWu: e.target.value })}
            />
          </div>
          {/* Tri-state fields */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">Capability Present?</label>
              <TristateSelect value={draft.capabilityPresent} onChange={(v) => setDraft({ ...draft, capabilityPresent: v })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">Capacity Confirmed?</label>
              <TristateSelect value={draft.capacityConfirmed} onChange={(v) => setDraft({ ...draft, capacityConfirmed: v })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">Committed?</label>
              <TristateSelect value={draft.committed} onChange={(v) => setDraft({ ...draft, committed: v })} />
            </div>
          </div>
          {/* Owner — a user reference where possible (migration 719); the text
              label remains for contacts who are not workspace members. */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
              Owner
              <span className="text-[var(--text-ter)] font-normal ml-1">(accountable contact)</span>
            </label>
            <Select
              value={draft.ownerUserId}
              onChange={(e) => {
                const userId = e.target.value;
                const member = members.find((m) => m.userId === userId);
                setDraft({
                  ...draft,
                  ownerUserId: userId,
                  owner: member ? member.name : draft.owner,
                });
              }}
              options={[
                { value: '', label: '— not a workspace member / type a name below —' },
                ...members.map((m) => ({ value: m.userId, label: m.name })),
              ]}
            />
            <input
              className="mt-2 w-full text-sm border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1.5 bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)]"
              placeholder="Display label for the contact"
              value={draft.owner}
              onChange={(e) => setDraft({ ...draft, owner: e.target.value })}
            />
          </div>
          {/* Participation request window + deadline */}
          <div className="border border-[var(--rule)] dark:border-[var(--rule)] p-3 space-y-3 bg-[var(--surface)]/80 dark:bg-[var(--surface)]">
            <p className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
              Launch participation request
            </p>
            <p className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)] leading-relaxed">
              Deadline drives overdue launchability. Committed window must cover the priority&apos;s
              target launch dates for the Launch Calendar to treat the team as launch-ready.
            </p>
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                Response deadline
              </label>
              <input
                type="date"
                className="w-full text-sm border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1.5 bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)]"
                value={draft.participationDeadline}
                onChange={(e) => setDraft({ ...draft, participationDeadline: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                  Committed window start
                </label>
                <input
                  type="date"
                  className="w-full text-sm border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1.5 bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)]"
                  value={draft.committedWindowStart}
                  onChange={(e) => setDraft({ ...draft, committedWindowStart: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                  Committed window end
                </label>
                <input
                  type="date"
                  className="w-full text-sm border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1.5 bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)]"
                  value={draft.committedWindowEnd}
                  onChange={(e) => setDraft({ ...draft, committedWindowEnd: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                Participation owner
                <span className="text-[var(--text-ter)] font-normal ml-1">(who answers this)</span>
              </label>
              <select
                className="w-full text-sm border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1.5 bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
                value={draft.participationOwnerId}
                disabled={sent}
                onChange={(e) => setDraft({ ...draft, participationOwnerId: e.target.value })}
              >
                <option value="">— nobody yet —</option>
                {members.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Once a request is SENT, its status is the counterpart's answer —
                not something the requester types. That one rule is what turns
                this from a self-reported checkbox into evidence, and it is
                enforced server-side (migration 690), not by hiding the field. */}
            {sent ? (
              <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] px-3 py-2 space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                    Request status
                  </span>
                  <span className="text-xs font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                    {row.respondedAt ? row.requestStatus : 'awaiting answer'}
                  </span>
                </div>
                <p className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)] leading-relaxed">
                  {row.respondedAt
                    ? `Answered ${new Date(row.respondedAt).toLocaleString()}${row.responseNote ? ` — “${row.responseNote}”` : ''}`
                    : mayWithdraw
                      ? 'Sent. Only the named participation owner can answer it — you can withdraw it.'
                      : 'Sent. Only the named participation owner can answer it, and only the person who sent it or a workspace admin can withdraw it.'}
                </p>
                {mayWithdraw && (
                  <button
                    type="button"
                    onClick={() => void handoff('withdraw')}
                    disabled={handoffBusy}
                    className="inline-flex items-center gap-1 text-xs font-medium text-[var(--coral)] hover:underline disabled:opacity-50"
                  >
                    {handoffBusy && <Loader2 className="h-3 w-3 animate-spin" />}
                    Withdraw request
                  </button>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                  Request status
                </label>
                <select
                  className="w-full text-sm border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1.5 bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
                  value={draft.requestStatus}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      requestStatus: e.target.value as ParticipationRequestStatus,
                      committed:
                        e.target.value === 'confirmed'
                          ? 'yes'
                          : e.target.value === 'tentative'
                            ? 'partial'
                            : 'no',
                    })
                  }
                >
                  {REQUEST_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => void handoff('send')}
                  disabled={handoffBusy || !draft.participationOwnerId}
                  title={
                    draft.participationOwnerId
                      ? 'Hand this request to its owner. From then on, only they can answer it.'
                      : 'Set a participation owner first — a request needs somebody to send it to.'
                  }
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {handoffBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                  Send request
                </button>
              </div>
            )}
            {handoffError && <p className="text-xs text-[var(--coral)]">{handoffError}</p>}
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                Period commitments ({draft.periodCommitments.length} weeks)
              </p>
              <button
                type="button"
                className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
                onClick={() => {
                  const periods = fillPeriodCommitmentsFromWindow(
                    draft.committedWindowStart || null,
                    draft.committedWindowEnd || null,
                    draft.requestStatus === 'confirmed' ? 'confirmed' : 'requested',
                    draft.capacityRequiredWu ? Number(draft.capacityRequiredWu) : null,
                  );
                  setDraft({
                    ...draft,
                    periodCommitments: periods,
                    requestStatus: rollupRequestStatus(periods, draft.requestStatus),
                  });
                }}
              >
                Fill weeks from window
              </button>
            </div>
            {draft.periodCommitments.length > 0 && (
              <div className="max-h-40 overflow-auto space-y-1.5">
                {draft.periodCommitments.map((p, idx) => (
                  <div
                    key={`${p.bucketStart}-${idx}`}
                    className="flex items-center gap-2 text-xs"
                  >
                    <span className="font-mono text-sm w-28 shrink-0 text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                      {p.bucketStart}
                    </span>
                    <select
                      className="flex-1 border border-[var(--rule)] dark:border-[var(--rule)] rounded px-1.5 py-1 bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
                      value={p.status}
                      onChange={(e) => {
                        const status = e.target.value as ParticipationRequestStatus;
                        const next = draft.periodCommitments.map((row, i) =>
                          i === idx ? { ...row, status } : row,
                        );
                        setDraft({
                          ...draft,
                          periodCommitments: next,
                          requestStatus: rollupRequestStatus(next, draft.requestStatus),
                          committed:
                            rollupRequestStatus(next) === 'confirmed'
                              ? 'yes'
                              : rollupRequestStatus(next) === 'tentative'
                                ? 'partial'
                                : 'no',
                        });
                      }}
                    >
                      {REQUEST_STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}
            {draft.periodCommitments.length >= 8 && (
              <p className="text-[11px] text-[var(--text-ter)]">
                {draft.periodCommitments.length} weeks — scroll to edit each period status.
              </p>
            )}
          </div>
          {/* Risk if Unavailable */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">Risk if Unavailable</label>
            <input
              className="w-full text-sm border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1.5 bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)]"
              placeholder="What happens if this team can't participate?"
              value={draft.riskIfUnavailable}
              onChange={(e) => setDraft({ ...draft, riskIfUnavailable: e.target.value })}
            />
          </div>
          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">Notes</label>
            <textarea
              rows={2}
              className="w-full text-sm border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1.5 bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)]"
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[var(--rule)] dark:border-[var(--rule)]">
          <button
            onClick={onCancel}
            className="px-3 py-2 text-sm border border-[var(--rule)] dark:border-[var(--rule)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving || !draft.priorityId || !draft.requiredTeam.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-medium"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

type ParticipationRow = TeamParticipation & {
  priorityName: string;
  workUnitTitle: string;
};

export default function TeamParticipationView() {
  const { projectId, activeScenario, error: engineError } = usePlanningEngine();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fromLaunchCalendar = searchParams.get('from') === 'launch-calendar';
  const [rows, setRows] = useState<ParticipationRow[]>([]);
  const [priorities, setPriorities] = useState<PriorityOption[]>([]);
  const [workUnits, setWorkUnits] = useState<WUOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<TeamParticipation | null>(null);
  // A request needs somebody to send it to, so the owner picker needs the roster.
  const [members, setMembers] = useState<MemberOption[]>([]);
  // The org chart backs the Required Team picker (hierarchy type `org`).
  const orgUnitsMap = useEnterpriseIdentityStore((s) => s.orgUnits);
  const fetchOrgUnits = useEnterpriseIdentityStore((s) => s.fetchFromSupabase);
  const orgUnits = useMemo(
    () => filterOrgUnitsForWorkspace(Array.from(orgUnitsMap.values()), projectId),
    [orgUnitsMap, projectId],
  );
  useEffect(() => {
    if (!projectId) return;
    void fetchOrgUnits({ workspaceId: projectId });
  }, [fetchOrgUnits, projectId]);
  const orgUnitPathLabel = useCallback(
    (id: string | null): string | null => {
      if (!id) return null;
      const unit = orgUnitsMap.get(id);
      return unit ? formatOrgUnitPathLabel(unit, orgUnitsMap) : null;
    },
    [orgUnitsMap],
  );
  const [draft, setDraft] = useState<DraftParticipation>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [returning, setReturning] = useState(false);
  // Next step after send / withdraw — what happens now, stated once.
  const [handoffNotice, setHandoffNotice] = useState<string | null>(null);

  const handleRerunAndReturn = async () => {
    if (!projectId || !activeScenario?.id) {
      navigate('/planning/launch-calendar');
      return;
    }
    setReturning(true);
    setError(null);
    try {
      await runTimePhasedPlacement(projectId, activeScenario.id);
      navigate('/planning/launch-calendar');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to re-run placement');
    } finally {
      setReturning(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [participation, prList, wuList, memberList] = await Promise.all([
        getTeamParticipation(projectId),
        getPriorities(projectId),
        getWorkUnits(projectId),
        // Narrowed here rather than adding a fourth `projectId` to this file's
        // long-standing string|null hole — the owner picker degrades to empty
        // without a workspace, which is the correct behaviour anyway.
        projectId ? listWorkspaceMembers(projectId).catch(() => []) : Promise.resolve([]),
      ]);
      setMembers(
        (memberList ?? [])
          .filter((m) => m.status === 'active')
          .map((m) => ({
            userId: m.userId,
            name: m.user?.name || m.user?.email || m.userId.slice(0, 8),
          })),
      );
      setPriorities(prList.map((p) => ({ id: p.id, name: p.name })));
      setWorkUnits(wuList.map((wu) => ({ id: wu.id, title: wu.title, priorityId: wu.priorityId })));
      const priorityNameById = new Map(prList.map((p) => [p.id, p.name]));
      const wuTitleById = new Map(wuList.map((wu) => [wu.id, wu.title]));
      setRows(participation.map((p) => ({
        ...p,
        priorityName: p.priorityId ? (priorityNameById.get(p.priorityId) ?? '—') : '—',
        workUnitTitle: p.workUnitId ? (wuTitleById.get(p.workUnitId) ?? '—') : 'ALL',
      })));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { void load(); }, [load]);

  const openCreate = () => {
    setEditTarget(null);
    setDraft(EMPTY_DRAFT);
    setShowModal(true);
  };

  const openEdit = (row: ParticipationRow) => {
    setEditTarget(row);
    setDraft(participationToDraft(row));
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Partial<TeamParticipation> = {
        projectId,
        priorityId: draft.priorityId || null,
        workUnitId: draft.workUnitId || null,
        requiredTeam: draft.requiredTeam.trim(),
        requiredOrgUnitId: draft.requiredOrgUnitId || null,
        requiredCapability: draft.requiredCapability.trim() || null,
        capacityRequiredWu: draft.capacityRequiredWu ? Number(draft.capacityRequiredWu) : null,
        capacityConfirmed: draft.capacityConfirmed,
        capabilityPresent: draft.capabilityPresent,
        committed: draft.committed,
        owner: draft.owner.trim() || null,
        ownerUserId: draft.ownerUserId || null,
        riskIfUnavailable: draft.riskIfUnavailable.trim() || null,
        notes: draft.notes.trim() || null,
        committedWindowStart: draft.committedWindowStart || null,
        committedWindowEnd: draft.committedWindowEnd || null,
        participationDeadline: draft.participationDeadline || null,
        participationOwnerId: draft.participationOwnerId || null,
        requestStatus: draft.requestStatus,
        periodCommitments: draft.periodCommitments,
      };
      if (editTarget) {
        await updateTeamParticipation(editTarget.id, payload);
      } else {
        await createTeamParticipation(projectId, payload);
      }
      setShowModal(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this participation requirement?')) return;
    setDeleting(id);
    try {
      await deleteTeamParticipation(id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(null);
    }
  };

  const tableColumns = useMemo((): TableColumnWithRender<ParticipationRow>[] => [
    {
      key: 'priorityName',
      label: 'Priority',
      sortable: true,
      minWidth: 180,
      flex: true,
      render: (r) => (
        <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate block">
          {r.priorityName}
        </span>
      ),
    },
    {
      key: 'workUnitTitle',
      label: 'Scope',
      render: (r) => (
        <span className={`text-xs ${r.workUnitId ? 'text-[var(--text-sec)] dark:text-[var(--text-sec)]' : 'text-[var(--text-ter)] dark:text-[var(--text-ter)] font-medium'}`}>
          {r.workUnitTitle}
        </span>
      ),
    },
    {
      key: 'requiredTeam',
      label: 'Required Team',
      sortable: true,
      render: (r) => {
        const path = orgUnitPathLabel(r.requiredOrgUnitId);
        return (
          <span className="text-sm font-medium inline-flex items-center gap-1.5" title={path ?? 'Not linked to an org unit'}>
            {r.requiredTeam}
            {path ? (
              <Building2 className="h-3 w-3 text-[var(--text-ter)]" aria-label="Linked to the org chart" />
            ) : (
              <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--text-dim)]" aria-label="Text only">text</span>
            )}
          </span>
        );
      },
    },
    {
      key: 'capabilityPresent',
      label: 'Capability',
      render: (r) => <TristateBadge value={r.capabilityPresent} />,
    },
    {
      key: 'capacityConfirmed',
      label: 'Capacity',
      render: (r) => <TristateBadge value={r.capacityConfirmed} />,
    },
    {
      key: 'committed',
      label: 'Committed',
      render: (r) => <TristateBadge value={r.committed} />,
    },
    {
      key: 'requestStatus',
      label: 'Request',
      render: (r) => {
        const n = r.periodCommitments?.length ?? 0;
        const confirmed = r.periodCommitments?.filter((p) => p.status === 'confirmed').length ?? 0;
        return (
          <span className="text-xs capitalize text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            {r.requestStatus}
            {n > 0 ? ` · ${confirmed}/${n} wk` : ''}
          </span>
        );
      },
    },
    {
      key: 'owner',
      label: 'Owner',
      render: (r) => <span className="text-xs text-[var(--text-ter)]">{r.owner ?? '—'}</span>,
    },
    {
      key: 'participationDeadline',
      label: 'Deadline',
      render: (r) => {
        const overdue =
          r.committed !== 'yes' &&
          !!r.participationDeadline &&
          r.participationDeadline < new Date().toISOString().slice(0, 10);
        return (
          <span
            className={`text-xs ${overdue ? 'text-[var(--coral)] font-medium' : 'text-[var(--text-ter)]'}`}
          >
            {r.participationDeadline ?? '—'}
            {overdue ? ' · overdue' : ''}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      render: (r) => (
        <div className="flex items-center gap-1">
          <button
            title="Edit"
            onClick={(e) => { e.stopPropagation(); openEdit(r); }}
            className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)]"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            title="Delete"
            disabled={deleting === r.id}
            onClick={(e) => { e.stopPropagation(); void handleDelete(r.id); }}
            className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)]"
          >
            {deleting === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </button>
        </div>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [deleting, orgUnitPathLabel]);

  const displayError = error ?? engineError;

  return (
    <>
      {handoffNotice && (
        <div role="status" className="mb-3 flex items-start gap-3 border border-[var(--rule)] bg-[var(--surface)] px-4 py-2.5 text-sm text-[var(--text-sec)]">
          <span className="flex-1">{handoffNotice}</span>
          <Button variant="ghost" size="sm" aria-label="Dismiss" onClick={() => setHandoffNotice(null)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      {fromLaunchCalendar && (
        <div className="mx-4 md:mx-6 mt-4 mb-0 border border-primary-200 dark:border-primary-800/40 bg-primary-50/60 dark:bg-primary-900/15 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <Calendar className="h-4 w-4 text-primary-700 dark:text-primary-400 mt-0.5 shrink-0" />
            <p className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              You came from the Launch Calendar. Confirm participation here, then return so
              launchability can update.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Link
              to="/planning/launch-calendar"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to Launch Calendar
            </Link>
            <button
              type="button"
              disabled={returning || !activeScenario}
              onClick={() => void handleRerunAndReturn()}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50"
            >
              {returning ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ArrowRight className="h-3.5 w-3.5" />
              )}
              Re-run placement &amp; return
            </button>
          </div>
        </div>
      )}
      <PlanningEntityListPage<ParticipationRow, string>
        items={rows}
        loading={loading}
        getId={(r) => r.id}
        title="Team Participation"
        subtitle={`${rows.length} requirement${rows.length !== 1 ? 's' : ''} · Cross-functional participation tracking`}
        icon={<Users className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBgClassName="bg-[var(--slate-dim)]"
        itemLabel="requirement"
        searchFields={['priorityName', 'workUnitTitle', 'requiredTeam', 'requiredCapability', 'owner']}
        sortOptions={[
          { field: 'priorityName', label: 'Priority' },
          { field: 'requiredTeam', label: 'Team' },
        ]}
        defaultSort={{ field: 'priorityName', direction: 'asc' }}
        facets={[
          {
            field: 'capabilityPresent',
            label: 'Capability',
            type: 'multi',
            options: [
              { value: 'yes', label: 'Yes' },
              { value: 'partial', label: 'Partial' },
              { value: 'no', label: 'No' },
            ],
          },
          {
            field: 'committed',
            label: 'Committed',
            type: 'multi',
            options: [
              { value: 'yes', label: 'Yes' },
              { value: 'partial', label: 'Partial' },
              { value: 'no', label: 'No' },
            ],
          },
        ]}
        tableColumns={tableColumns}
        renderDetail={(r) => (
          <div className="p-6 space-y-4 h-full overflow-auto text-sm">
            <h3 className="text-base font-semibold">{r.requiredTeam}</h3>
            <dl className="grid grid-cols-2 gap-3">
              <div>
                <dt className="text-xs text-[var(--text-ter)]">Priority</dt>
                <dd>{r.priorityName}</dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--text-ter)]">Scope</dt>
                <dd className={r.workUnitId ? '' : 'text-[var(--text-ter)] font-medium'}>{r.workUnitTitle}</dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--text-ter)]">Org unit</dt>
                <dd>{orgUnitPathLabel(r.requiredOrgUnitId) ?? <span className="text-[var(--text-ter)]">not linked</span>}</dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--text-ter)]">Required Capability</dt>
                <dd>{r.requiredCapability ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--text-ter)]">Capacity Required (WU)</dt>
                <dd>{r.capacityRequiredWu ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--text-ter)]">Capability Present</dt>
                <dd><TristateBadge value={r.capabilityPresent} /></dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--text-ter)]">Capacity Confirmed</dt>
                <dd><TristateBadge value={r.capacityConfirmed} /></dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--text-ter)]">Committed</dt>
                <dd><TristateBadge value={r.committed} /></dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--text-ter)]">Owner</dt>
                <dd>{r.owner ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--text-ter)]">Response deadline</dt>
                <dd>{r.participationDeadline ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--text-ter)]">Committed window</dt>
                <dd>
                  {r.committedWindowStart || r.committedWindowEnd
                    ? `${r.committedWindowStart ?? '—'} → ${r.committedWindowEnd ?? '—'}`
                    : '—'}
                </dd>
              </div>
              {r.riskIfUnavailable && (
                <div className="col-span-2">
                  <dt className="text-xs text-[var(--text-ter)]">Risk if Unavailable</dt>
                  <dd className="text-[var(--mustard)] dark:text-[var(--mustard)] mt-1">{r.riskIfUnavailable}</dd>
                </div>
              )}
              {r.notes && (
                <div className="col-span-2">
                  <dt className="text-xs text-[var(--text-ter)]">Notes</dt>
                  <dd className="text-[var(--text-sec)] mt-1">{r.notes}</dd>
                </div>
              )}
              <div className="col-span-2 pt-2 flex gap-2">
                <button
                  onClick={() => openEdit(r)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs border border-[var(--rule)] dark:border-[var(--rule)] rounded hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
                >
                  <Pencil className="h-3 w-3" /> Edit
                </button>
                <button
                  onClick={() => void handleDelete(r.id)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs border border-[var(--coral)] text-[var(--coral)] rounded hover:bg-[var(--coral-dim)]"
                >
                  <Trash2 className="h-3 w-3" /> Delete
                </button>
              </div>
            </dl>
          </div>
        )}
        selectedId={selectedId}
        onSelectionChange={setSelectedId}
        primaryAction={{
          label: 'Add Requirement',
          onClick: openCreate,
        }}
        emptyTitle="No participation requirements yet"
        emptyDescription="Add cross-functional participation requirements to track capability, capacity, and commitment for each priority."
        emptyAction={{ label: 'Add Requirement', onClick: openCreate }}
        error={displayError}
      />
      {showModal && (
        <ParticipationModal
          title={editTarget ? 'Edit Participation Requirement' : 'Add Participation Requirement'}
          draft={draft}
          setDraft={setDraft}
          priorities={priorities}
          workUnits={workUnits}
          members={members}
          orgUnits={orgUnits}
          workspaceId={projectId}
          row={editTarget}
          saving={saving}
          onSave={() => void handleSave()}
          onCancel={() => setShowModal(false)}
          onHandedOff={(action) => {
            setShowModal(false);
            setHandoffNotice(
              action === 'send'
                ? 'Request sent. The named owner has been told, and you will get a notice when they answer. Until then this priority is placed as if they will take part.'
                : 'Request withdrawn. The owner has been told. Re-run placement to see the plan without it.',
            );
            void load();
          }}
        />
      )}
    </>
  );
}
